import {
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { ALL_WEEKDAYS, WeekdayStr } from "rrule";

import DatePicker from "~/components/common/forms/DatePicker";
import { WeeklyRRule } from "@caredna-marlin/utils/client/availability";
import { datef } from "@caredna-marlin/utils/dates";
import Help from "~/components/common/Help";

const sortByWeekday = (a: WeekdayStr, b: WeekdayStr) => ALL_WEEKDAYS.indexOf(a) - ALL_WEEKDAYS.indexOf(b)

type RepeatDailyFormProps = {
  disabled?: boolean;
  rrule: WeeklyRRule;
  setRRule: (rrule: WeeklyRRule) => void;
  startLocked: boolean;
  untilHelpText?: string;
};

const RepeatWeeklyForm = ({ disabled, rrule, setRRule, startLocked, untilHelpText }: RepeatDailyFormProps) => {
  const setDays = (_: any, days: string[]) => {
    setRRule({ ...rrule, byweekday: (days as WeekdayStr[]).filter(Boolean).sort(sortByWeekday)});
  }

  // Breaking the rules of react state management to work around a bug in MUI datepicker.
  // Clicking the "X" to clear the end date causes both onClear to be called from the input element,
  // but then *also* onChange - with the previously selected date - to be called from the DatePicker directly.
  // This results in `until` being cleared out, but then immediately set again. Whoops.

  // So instead we use a locally scoped variable as a flag to communicate between the two callbacks.
  // This is discarded on every component render - it does *not* persist, intentionally so.
  let isClearing = false;

  return <>
    <Grid size={3}></Grid>
    <Grid size={9} sx={{ mb: 1 }}>
      <ToggleButtonGroup
        value={rrule.byweekday}
        onChange={setDays}
        aria-label="text formatting"
        disabled={disabled}
      >
        <ToggleButton value="SU" aria-label="Sunday">S</ToggleButton>
        <ToggleButton value="MO" aria-label="Monday">M</ToggleButton>
        <ToggleButton value="TU" aria-label="Tuesday">T</ToggleButton>
        <ToggleButton value="WE" aria-label="Wednesday">W</ToggleButton>
        <ToggleButton value="TH" aria-label="Thursday">T</ToggleButton>
        <ToggleButton value="FR" aria-label="Friday">F</ToggleButton>
        <ToggleButton value="SA" aria-label="Saturday">S</ToggleButton>
      </ToggleButtonGroup>
    </Grid>

    <Grid size={3}><span>Starting on</span></Grid>
    <Grid size={9} sx={{ pl: startLocked ? "6px" : undefined }}>
      {startLocked ? <>
        {datef(rrule.dtstart, "MM/dd/yyyy")} <Help text="Cannot edit the start date in the past" />
      </> : <DatePicker
        disabled={disabled}
        disablePast
        value={rrule.dtstart}
        sx={{ width: "100%" }}
        onChange={(dtstart) => dtstart?.getTime() && setRRule({...rrule, dtstart})}
      />}
    </Grid>
    <Grid size={3}><span>Until {untilHelpText && <Help text={untilHelpText} />}</span></Grid>
    <Grid size={9}>
      <DatePicker
        slotProps={{
          field: {
            clearable: true,
            onClear: () => {
              setRRule({...rrule, until: undefined})
              isClearing = true
            },
          },
          textField: {
            placeholder: "Indefinite",
          }
        }}
        disabled={disabled}
        disablePast
        value={rrule.until || null}
        sx={{ width: "100%" }}
        onChange={(until) => until?.getTime() && !isClearing && setRRule({...rrule, until})}
      />
    </Grid>
  </>
}

export default RepeatWeeklyForm;
